import React, {useEffect, useState} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ExcelButton } from './excel_button';

const ITEMS_PER_PAGE = 50;

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

export const Locations = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const logoutUser = () => {
    dispatch({ type: 'LOG_OUT', payload: null });
    sessionStorage.setItem('currentUser', null);
    window.location = "/";
  };

  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filterLocations = (data, term) => {
    if (!term) return data;
    return data.filter(item => {
      if (!item) return false;
      const fields = [
        item[28] ? item[28].value : '',
        item[8] ? item[8].value : '',
        item[15] ? item[15].value : '',
        item[102] ? item[102].value : '',
      ];
      return fields.some(val => val.toLowerCase().includes(term));
    });
  };

  const sortLocations = (data) => {
    return [...data].sort((a, b) => {
      const codeA = a && a[28] && a[28].value ? String(a[28].value) : '';
      const codeB = b && b[28] && b[28].value ? String(b[28].value) : '';
      const numA = parseInt((codeA.match(/\d+/g) || []).join(''), 10);
      const numB = parseInt((codeB.match(/\d+/g) || []).join(''), 10);
      const safeA = isNaN(numA) ? Infinity : numA;
      const safeB = isNaN(numB) ? Infinity : numB;
      if (safeA !== safeB) return safeA - safeB;
      return codeA.localeCompare(codeB);
    });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchInitial = async () => {
      setIsLoading(true);
      const response = await fetch("/apilocations?limit=50", { credentials: 'include' });
      const data = await response.json();
      if (data && data.sessionExpired) { logoutUser(); return; }
      if (!isMounted) return;
      const sorted = sortLocations(data);
      setLocations(sorted);
      setFilteredLocations(sorted);
      setIsLoading(false);

      // Phase 2: fetch all records in background
      const allResponse = await fetch("/apilocations", { credentials: 'include' });
      const allData = await allResponse.json();
      if (allData && allData.sessionExpired) { logoutUser(); return; }
      if (!isMounted) return;
      const allSorted = sortLocations(allData);
      setLocations(allSorted);
      setFilteredLocations(searchTerm ? filterLocations(allSorted, searchTerm) : allSorted);
      setIsFullyLoaded(true);
    };

    fetchInitial();
    return () => { isMounted = false; };
  }, []);

  const handleChangeInput = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setFilteredLocations(filterLocations(sortLocations(locations), term));
    setCurrentPage(1);
  };

  // Pagination
  const totalPages = Math.ceil(filteredLocations.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedLocations = filteredLocations.slice(startIndex, endIndex);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredLocations.length / ITEMS_PER_PAGE));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredLocations, currentPage]);

  const headers = (
    <tr>
      <th>Location Code</th>
      <th>Address</th>
      <th>Phone Number</th>
      <th>Timezone</th>
    </tr>
  );

  const exportRows = () => filteredLocations.map(loc => ({
    'Location Code': loc && loc[28] ? loc[28].value : '',
    'Address': loc && loc[8] ? loc[8].value : '',
    'Phone Number': loc && loc[15] ? loc[15].value : '',
    'Timezone': loc && loc[102] ? loc[102].value : '',
  }));

  return (
  <div className="locations">
  <div className="locationsList">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
      <h2 style={{ margin: 0 }}>Locations</h2>
      <ExcelButton rows={exportRows} fileName="locations.xlsx" sheetName="Locations" />
    </div>
    <div className="searchInputWrapper inputWrapper" style={{ marginTop: '16px' }}>
      <input placeholder="Search locations..." onChange={handleChangeInput}></input>
    </div>
    {isLoading ? (
      <div className="skeleton-wrapper">
        <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
          Loading locations...
        </p>
        <table className="skeleton-table">
          <tbody>
            {headers}
            {Array.from({ length: 15 }).map((_, idx) => (
              <tr key={idx}>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-long"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <>
        <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
          Showing {filteredLocations.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredLocations.length)} of {filteredLocations.length} locations
          {!isFullyLoaded && <span> (loading more...)</span>}
        </p>
        <table id="tableWrapper">
        <tbody>
        {headers}
          {paginatedLocations.length > 0 && paginatedLocations.map((location, idx) => {
            return (
              <tr key={idx} onClick={() => navigate(`/locationDetail/${location[3]?.value}`)} className="tableRow">
                <td className="tableCell">{location && location[28] ? location[28].value : ""}</td>
                <td className="tableCell">{location && location[8] ? location[8].value : ""}</td>
                <td className="tableCell">{location && location[15] ? location[15].value : ""}</td>
                <td className="tableCell">{location && location[102] ? location[102].value : ""}</td>
              </tr>
            )
          })}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <button
              className={`pagination-btn ${currentPage === 1 ? 'pagination-btn--disabled' : ''}`}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {getPageNumbers(currentPage, totalPages).map((page, idx) => (
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="pagination-btn pagination-btn--disabled">...</span>
              ) : (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'pagination-btn--active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              )
            ))}
            <button
              className={`pagination-btn ${currentPage === totalPages ? 'pagination-btn--disabled' : ''}`}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </>
    )}
  </div>
  </div>
  )
}
